import React, { useState } from 'react'
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress
} from '@mui/material'
import {
  MenuBook as ReadingIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { motion } from 'framer-motion'
import { apiClient, API_ENDPOINTS, buildApiUrl } from '../config/api'
import toast from 'react-hot-toast'

const readingLevels = [
  { level: 'A1', title: 'Beginner', description: 'Văn bản đơn giản, từ vựng cơ bản' },
  { level: 'A2', title: 'Elementary', description: 'Văn bản ngắn về chủ đề quen thuộc' },
  { level: 'B1', title: 'Intermediate', description: 'Văn bản về chủ đề thường gặp' },
  { level: 'B2', title: 'Upper-Intermediate', description: 'Văn bản phức tạp, chuyên môn' },
  { level: 'C1', title: 'Advanced', description: 'Văn bản học thuật, chuyên sâu' }
]

function Reading() {
  const [selectedLevel, setSelectedLevel] = useState('B1')
  const [readingTest, setReadingTest] = useState(null)
  const [userAnswers, setUserAnswers] = useState({})
  const [showResults, setShowResults] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [score, setScore] = useState(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')

  const handleStreamingData = (data) => {
    if (data.type === 'start') {
      setStreamingContent('')
    } else if (data.type === 'chunk') {
      setStreamingContent(prev => prev + data.content)
    } else if (data.type === 'complete') {
      // Use a callback to ensure we get the latest streamingContent
      setStreamingContent(currentContent => {
        try {
          // Try to parse the complete content as JSON
          const jsonMatch = currentContent.match(/```(?:json)?\s*([\s\S]*?)\s*```/)
          if (jsonMatch) {
            const testData = JSON.parse(jsonMatch[1].trim())
            setReadingTest({
              ...testData,
              level: selectedLevel,
              timestamp: new Date().toISOString()
            })
            toast.success('Đã tạo bài test đọc hiểu!')
          } else {
            // Fallback: use the raw content
            setReadingTest({
              passage: currentContent,
              questions: [],
              level: selectedLevel,
              timestamp: new Date().toISOString()
            })
            toast.success('Đã tạo bài test đọc hiểu!')
          }
        } catch (error) {
          console.error('Error parsing streaming content:', error)
          console.log('Current content:', currentContent) // Debug log
          toast.error('Có lỗi xảy ra khi xử lý dữ liệu')
        }
        
        // Clear the content after processing
        setIsStreaming(false)
        return '' // Clear the streaming content
      })
    } else if (data.type === 'error') {
      toast.error('Có lỗi xảy ra: ' + data.error)
      setIsStreaming(false)
      setStreamingContent('')
    }
  }

  const handleGenerateTest = async () => {
    setIsLoading(true)
    setIsStreaming(true)
    setReadingTest(null)
    setUserAnswers({})
    setShowResults(false)
    setScore(null)
    setStreamingContent('')

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.ASSESSMENT.READING_STREAM), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          level: selectedLevel
        })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              handleStreamingData(data)
            } catch (error) {
              console.error('Error parsing SSE data:', error)
            }
          }
        }
      }
    } catch (error) {
      console.error('Reading test generation error:', error)
      toast.error('Có lỗi xảy ra khi tạo bài test')
      setIsStreaming(false)
      setStreamingContent('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAnswerChange = (questionIndex, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }))
  }

  const handleSubmitTest = () => {
    if (!readingTest || !readingTest.questions) return

    let correctAnswers = 0
    const totalQuestions = readingTest.questions.length

    readingTest.questions.forEach((question, index) => {
      if (userAnswers[index] === question.correctAnswer) {
        correctAnswers++
      }
    })

    const finalScore = Math.round((correctAnswers / totalQuestions) * 100)
    setScore({ correct: correctAnswers, total: totalQuestions, percentage: finalScore })
    setShowResults(true)
    
    if (finalScore >= 80) {
      toast.success(`Xuất sắc! Bạn đạt ${finalScore}%`)
    } else if (finalScore >= 60) {
      toast.success(`Tốt! Bạn đạt ${finalScore}%`)
    } else {
      toast.error(`Cần cải thiện! Bạn đạt ${finalScore}%`)
    }
  }

  const allQuestionsAnswered = readingTest?.questions?.every((_, index) => 
    userAnswers[index] !== undefined
  )

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <ReadingIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
            Luyện đọc hiểu
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Cải thiện kỹ năng đọc hiểu tiếng Anh qua các bài test theo trình độ
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Level Selection */}
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Chọn trình độ
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {readingLevels.map((levelInfo) => (
                  <Card 
                    key={levelInfo.level}
                    sx={{ 
                      cursor: 'pointer',
                      border: selectedLevel === levelInfo.level ? 2 : 1,
                      borderColor: selectedLevel === levelInfo.level ? 'primary.main' : 'divider',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                    onClick={() => setSelectedLevel(levelInfo.level)}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Chip label={levelInfo.level} size="small" color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {levelInfo.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {levelInfo.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
              
              <Button
                fullWidth
                variant="contained"
                onClick={handleGenerateTest}
                disabled={isLoading || isStreaming}
                startIcon={<QuizIcon />}
                sx={{ mt: 2 }}
              >
                {isStreaming ? '🔄 Đang tạo bài test...' : isLoading ? 'Đang tạo bài test...' : 'Tạo bài test mới'}
              </Button>
            </Paper>

            {score && (
              <Paper elevation={3} sx={{ p: 3, bgcolor: score.percentage >= 60 ? 'success.50' : 'error.50' }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Kết quả
                </Typography>
                <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
                  {score.percentage}%
                </Typography>
                <Typography variant="body2">
                  {score.correct}/{score.total} câu đúng
                </Typography>
              </Paper>
            )}
          </Grid>

          {/* Reading Test */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 3, minHeight: '70vh' }}>
              {isLoading && <LinearProgress sx={{ mb: 2 }} />}
              
              {/* Streaming Content Display */}
              {isStreaming && streamingContent && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Alert severity="info" sx={{ mb: 3 }}>
                    🔄 Đang tạo bài test đọc hiểu...
                  </Alert>
                  <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
                    <Typography variant="body1" sx={{ lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                      {streamingContent}
                    </Typography>
                  </Paper>
                </motion.div>
              )}
              
              {readingTest && !isStreaming ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Reading Passage */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Đoạn văn {console.log(readingTest)}
                    </Typography>
                    <Paper elevation={1} sx={{ p: 3, bgcolor: 'grey.50' }}>
                      <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                        {readingTest.passage}
                      </Typography>
                    </Paper>
                  </Box>

                  {/* Questions */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Câu hỏi ({readingTest.questions?.length || 0} câu)
                    </Typography>
                    {readingTest.questions?.map((question, index) => (
                      <Card key={index} sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                            {index + 1}. {question.question}
                          </Typography>
                          <RadioGroup
                            value={userAnswers[index] || ''}
                            onChange={(e) => handleAnswerChange(index, e.target.value)}
                          >
                            {question.options?.map((option, optionIndex) => (
                              <FormControlLabel
                                key={optionIndex}
                                value={option}
                                control={<Radio />}
                                label={option}
                                disabled={showResults}
                                sx={{
                                  '& .MuiFormControlLabel-label': {
                                    color: showResults ? (
                                      option === question.correctAnswer ? 'success.main' :
                                      option === userAnswers[index] && option !== question.correctAnswer ? 'error.main' :
                                      'text.primary'
                                    ) : 'text.primary'
                                  }
                                }}
                              />
                            ))}
                          </RadioGroup>
                          {showResults && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                              {userAnswers[index] === question.correctAnswer ? (
                                <CheckIcon sx={{ color: 'success.main', mr: 1 }} />
                              ) : (
                                <CancelIcon sx={{ color: 'error.main', mr: 1 }} />
                              )}
                              <Typography variant="body2" color="text.secondary">
                                Đáp án đúng: {question.correctAnswer}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  {/* Submit Button */}
                  {!showResults && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        size="large"
                        onClick={handleSubmitTest}
                        disabled={!allQuestionsAnswered}
                        startIcon={<CheckIcon />}
                      >
                        Nộp bài
                      </Button>
                    </Box>
                  )}

                  {showResults && (
                    <Box sx={{ textAlign: 'center' }}>
                      <Button
                        variant="outlined"
                        onClick={handleGenerateTest}
                        startIcon={<RefreshIcon />}
                      >
                        Làm bài test mới
                      </Button>
                    </Box>
                  )}
                </motion.div>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <ReadingIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                    Chọn trình độ và tạo bài test đọc hiểu
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    AI sẽ tạo đoạn văn và câu hỏi phù hợp với trình độ của bạn
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </motion.div>
    </Container>
  )
}

export default Reading